import { Component, EventEmitter, Input, OnDestroy, Output, TemplateRef } from '@angular/core';
import { filter, of, Subscription } from 'rxjs';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { TextInputDialogModel } from '../../../../Common/Dialogs/models/text-input-dialog.model';
import { TextInputDialogComponent } from '../../../../Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ListMembershipApiModel } from '../../../../api/targeting/model/list-membership-api-model';
import { UserNoteModel } from '../../../../Common/Misc/models/user-note.model';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import moment from 'moment-timezone';
import { timeZones } from '../../../../Common/Forms/data/time-zones';
import { ResourceActionTypes } from '../../../Basic/enums/resource-actions.enum';
import { ControlValueAccessorModel, getProvider } from '../../../Forms/models/control-value-accessor.model';


export interface NotesAllLayerUpdate {
    entityId: string;
    entityType: EntityType;
    notes: UserNoteModel[];
}

export interface NotesChange {
    notes: UserNoteModel[];
    type: ResourceActionTypes;
}

@Component({
    selector: 'agpr-user-notes',
    templateUrl: './user-notes.component.html',
    styleUrls: ['./user-notes.component.scss'],
    providers: [getProvider(UserNotesComponent)],
    standalone: false
})
export class UserNotesComponent extends ControlValueAccessorModel<UserNoteModel[]> implements OnDestroy {

    @Input() set userNotes(notes: UserNoteModel[]) {
        if (!notes) {
            return;
        }
        this._userNotes = notes.sort((a, b) => b.creationDate.getTime() - a.creationDate.getTime()).slice();
        this.setCreationDateDisplayStrings();
    }
    @Input() readonly: boolean = false;
    @Input() selfModificationOnly: boolean = false;
    @Input() id: string;
    @Input() currentUser: UserModel;
    @Input() inlineEditing: boolean = true;
    @Input() scrolling: boolean = true;
    @Input() disabled: boolean = false;
    @Input() disabledWarningTemplate: TemplateRef<any> = null;
    @Input() maxLength: number = 2000;
    @Input() displayOffset: boolean = false;
    @Input() offsetPrefix: string = 'UTC';
    @Input() hideAddButton: boolean = false;
    @Input() buttonStyle?:  ButtonTypesEnum;
    @Input() panelStyle?: boolean = false;
    @Input() isNotesTab?: boolean = false;

    @Output() notesChanged: EventEmitter<NotesChange> = new EventEmitter<NotesChange>(null);
    @Output() deleteNotes: EventEmitter<NotesChange> = new EventEmitter<NotesChange>(null);

    _userNotes: UserNoteModel[] = [];
    editingNote: UserNoteModel;
    noteText: string = '';
    cachedNoteText: string = '';
    usersMap: {[key: number]: string} = [];
    currentTime: string;
    userTimeZonePreference: string = '';
    showAddArea: boolean = false;
    listMemberships: ListMembershipApiModel[] = [];
    timezoneOffset: number;
    dateFormat: string;

    subscriptions: Subscription[] = [];
    public readonly buttonTypes = ButtonTypesEnum;
    public readonly messageSeverityEnum = messageSeverityEnum;

    constructor(
        public usersService: UsersService,
        public currentUserStateService: CurrentUserStateService,
        public coreDialogService: CoreDialogService
    ) {
        super();
        this.subscriptions.push(
            this.currentUserStateService.getAccountUsers().subscribe(users => {
                this.usersMap = {};

                if (users) {
                    users.forEach(user => {
                        this.usersMap[user.id] = user.getDisplayName();
                    });
                }
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(preferences => {
                this.userTimeZonePreference = preferences.timeZone;
                const timeZone = timeZones.find(x => x.agilityIds.includes(this.userTimeZonePreference) || x.momentId === this.userTimeZonePreference);
                if (timeZone) {
                    this.timezoneOffset = timeZone.gmtOffset;
                }
                this.dateFormat = preferences.getDateFormatString(true, true, true);
                this.setCreationDateDisplayStrings();
            })
        );
    }

    writeValue(value: UserNoteModel[]): void {
        if (!value) {
            return;
        }
        this._userNotes = value.sort((a, b) => b.creationDate.getTime() - a.creationDate.getTime()).slice();
        this.setCreationDateDisplayStrings();
    }

    registerOnChange(fn: (value: UserNoteModel[]) => void): void {
        this.onModelChange = fn;
    }

    ngOnDestroy() {
        this.subscriptions.map(subscription => subscription.unsubscribe());
    }

    addNote() {
        if (this.inlineEditing) {
            this.showAddArea = true;
            this.currentTime = this.setCreationDateDisplayString(new Date());
        } else {
            const io: TextInputDialogModel = {
                inputs: {
                    heading: 'Discover.userNotes.addNote',
                    description: 'Discover.userNotes.dialog.label',
                    placeholder: 'Discover.userNotes.placeholder',
                    okLabel: 'Discover.userNotes.dialog.addLabel',
                    maxLength: 2000,
                    useTextarea: true,
                    id: 'addNote',
                    submit: (noteText: string) => {
                        this.doneAddingNote(noteText);
                        return of(null);
                    }
                }
            };
            this.coreDialogService.open(TextInputDialogComponent, io);
        }
    }

    doneAddingNote(noteText: string) {
        if (noteText?.trim().length > 0) {
            const newNote = new UserNoteModel({
                value: noteText.trim(),
                creationDate: (new Date()).getTime(),
                createdUserId: this.currentUser.id
            });
            newNote.creationDateDisplayString = this.setCreationDateDisplayString(newNote.creationDate);
            this._userNotes.unshift(newNote);
            this.onModelChange(this._userNotes);
            this.notesChanged.emit({notes: this._userNotes, type: ResourceActionTypes.ADD_NOTE});
        }
        if (this.inlineEditing) {
            this.showAddArea = false;
            this.noteText = null;
        }
    }

    editNote(note: UserNoteModel) {
        this.editingNote = note;
        this.noteText = note.value;
        this.cachedNoteText = note.value;
        if (!this.inlineEditing) {
            const io: TextInputDialogModel = {
                inputs: {
                    heading: 'Discover.userNotes.editNote',
                    description: 'Discover.userNotes.dialog.label',
                    placeholder: 'Discover.userNotes.placeholder',
                    okLabel: 'Discover.userNotes.dialog.editLabel',
                    maxLength: 2000,
                    useTextarea: true,
                    id: 'editNote',
                    initialValue: note.value,
                    submit: (noteText: string) => {
                        this.doneEditingNote(noteText);
                        return of(null);
                    }
                },
                outputs: {
                    dialogClose: () => {
                        this.editingNote = null;
                        this.noteText = null;
                        this.cachedNoteText = null;
                    }
                }
            };
            this.coreDialogService.open(TextInputDialogComponent, io);
        }
    }

    doneEditingNote(noteText: string) {
        const editedNote = this._userNotes.find(x => x === this.editingNote);
        const noteLength = noteText?.trim().length;

        if (noteLength > 0) {
            editedNote.value = noteText;
            this.noteText = null;
        }

        if (this.cachedNoteText !== noteText && noteLength > 0) {
            editedNote.creationDate = new Date();
            editedNote.createdUserId = this.currentUser.id;
            editedNote.creationDateDisplayString = this.setCreationDateDisplayString(editedNote.creationDate);
            this._userNotes = this._userNotes.sort((a, b) => b.creationDate.getTime() - a.creationDate.getTime());
            this.onModelChange(this._userNotes);
            this.notesChanged.emit({notes: this._userNotes, type: ResourceActionTypes.EDIT_NOTE});
        }
        this.editingNote = null;
        this.cachedNoteText = null;
    }

    deleteNote(note: UserNoteModel) {
        const index = this._userNotes.findIndex(x => x === note);
        if (index >= 0) {
            this._userNotes.splice(index, 1);
        }
        this.onModelChange(this._userNotes);
        this.notesChanged.emit({ notes: this._userNotes , type: ResourceActionTypes.DELETE_NOTE });
    }

    setCreationDateDisplayStrings(): void {
        if (!this._userNotes || !this.userTimeZonePreference) {
            return;
        }
        this._userNotes.forEach(note => {
            note.creationDateDisplayString = this.isNotesTab? this.formatDate(note.creationDate): this.setCreationDateDisplayString(note.creationDate);
        });
    }

    setCreationDateDisplayString(creationDate: Date): string {
        let creationDateDisplayString = moment(creationDate).tz(this.userTimeZonePreference).format(this.dateFormat);
        if (this.displayOffset && !creationDateDisplayString.endsWith(this.offsetPrefix)) {
            let gmtOffset = '';
            if (this.timezoneOffset && this.timezoneOffset !== 0) {
                let hours: number|string = Math.trunc(this.timezoneOffset);
                if (hours >= 0) {
                    hours = '+' + hours;
                }

                let minutes: number|string = Math.abs(this.timezoneOffset) % 1;
                minutes = Math.round(minutes * 60);
                if (minutes < 10) {
                    minutes = '0' + minutes;
                }

                gmtOffset = `${hours}:${minutes}`;
            }
            creationDateDisplayString += ` (${this.offsetPrefix}${gmtOffset})`;
        }

        return creationDateDisplayString;
    }

    formatDate(date: Date): string {
        return moment(date)
            .format('MMMM D, YYYY [at] h:mm A z');
    }

}
